import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import RestrictionBadge from "../components/RestrictionBadge";
import EditPlaceModal from "../components/EditPlaceModal";
import EditReviewModal from "../components/EditReviewModal";
import EditTopicModal from "../components/EditTopicModal";
import EditBannerModal from "../components/EditBannerModal";
import CreateTopicModal from "../components/CreateTopicModal";
import CreateBannerModal, { PLACEMENTS } from "../components/CreateBannerModal";
import { toast } from "sonner";
import { Check, X, Star, Pencil, Trash2, MapPin, BookOpen, Megaphone, Plus, LayoutTemplate } from "lucide-react";
import EditFooterModal from "../components/EditFooterModal";

const STATUS_STYLES = {
  approved: { bg: "#E1F2E3", text: "#276832", label: "Aprovado" },
  pending: { bg: "#FCE9D2", text: "#A35C14", label: "Pendente" },
  rejected: { bg: "#FCD8D8", text: "#8B1A1A", label: "Rejeitado" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return <span style={{ background: s.bg, color: s.text }} className="px-3 py-1 rounded-full text-xs font-bold">{s.label}</span>;
}

export default function Moderation() {
  const [pendingPlaces, setPendingPlaces] = useState([]);
  const [allPlaces, setAllPlaces] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [topics, setTopics] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [creatingBanner, setCreatingBanner] = useState(false);
  const [editingFooter, setEditingFooter] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pp, ap, ar, tp, bn] = await Promise.all([
        api.get("/moderation/pending-places"),
        api.get("/moderation/places"),
        api.get("/moderation/reviews"),
        api.get("/topics"),
        api.get("/banners"),
      ]);
      setPendingPlaces(pp.data);
      setAllPlaces(ap.data);
      setAllReviews(ar.data);
      setTopics(tp.data);
      setBanners(bn.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actPlace = async (id, action) => {
    try {
      await api.post(`/moderation/places/${id}`, { action });
      toast.success(action === "approve" ? "Lugar aprovado" : "Lugar rejeitado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const actReview = async (id, action) => {
    try {
      await api.post(`/moderation/reviews/${id}`, { action });
      toast.success(action === "approve" ? "Avaliação aprovada" : "Avaliação rejeitada");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deletePlace = async (id) => {
    try {
      await api.delete(`/moderation/places/${id}`);
      toast.success("Lugar excluído");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/moderation/reviews/${id}`);
      toast.success("Avaliação excluída");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteTopic = async (id) => {
    try {
      await api.delete(`/moderation/topics/${id}`);
      toast.success("Tópico excluído");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteBanner = async (id) => {
    try {
      await api.delete(`/moderation/banners/${id}`);
      toast.success("Banner excluído");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const placementLabel = (v) => PLACEMENTS.find((p) => p.value === v)?.label || v || "—";

  const pendingReviewsCount = allReviews.filter((r) => r.status === "pending").length;

  return (
    <div data-testid="moderation-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-secondary">Moderação</span>
      <h1 className="font-heading font-black text-4xl text-brand-text mt-2">Painel de moderação</h1>
      <p className="mt-3 text-brand-textMuted">Revise envios pendentes e gerencie todos os lugares cadastrados.</p>

      <Tabs defaultValue="pending" className="mt-10">
        <TabsList className="bg-white border border-brand-border rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-pending" value="pending" className="rounded-full font-bold data-[state=active]:bg-brand-primary data-[state=active]:text-white px-5">
            Pendentes ({pendingPlaces.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-all-places" value="all" className="rounded-full font-bold data-[state=active]:bg-brand-primary data-[state=active]:text-white px-5">
            Todos os lugares ({allPlaces.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-reviews" value="reviews" className="rounded-full font-bold data-[state=active]:bg-brand-primary data-[state=active]:text-white px-5">
            Avaliações ({allReviews.length}{pendingReviewsCount > 0 ? ` · ${pendingReviewsCount} pendentes` : ""})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-topics" value="topics" className="rounded-full font-bold data-[state=active]:bg-brand-primary data-[state=active]:text-white px-5">
            Tópicos ({topics.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-banners" value="banners" className="rounded-full font-bold data-[state=active]:bg-brand-primary data-[state=active]:text-white px-5">
            Banners ({banners.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-footer" value="footer" className="rounded-full font-bold data-[state=active]:bg-brand-primary data-[state=active]:text-white px-5">
            Rodapé
          </TabsTrigger>
        </TabsList>

        {/* PENDING PLACES */}
        <TabsContent value="pending" className="mt-6">
          {loading ? <p>Carregando...</p> : pendingPlaces.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-brand-border p-12 text-center text-brand-textMuted">Nada pendente. Tudo em dia! 🎉</div>
          ) : (
            <div className="grid gap-4">
              {pendingPlaces.map((p) => (
                <div key={p.place_id} data-testid={`pending-place-${p.place_id}`} className="rounded-3xl bg-white border border-brand-border p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-1.5 mb-2">{(p.restrictions || []).map((r) => <RestrictionBadge key={r} type={r} />)}</div>
                      <h3 className="font-heading font-bold text-xl text-brand-text">{p.name}</h3>
                      <p className="text-sm text-brand-textMuted mt-1">{p.address}, {p.city} · {p.state}</p>
                      <p className="text-sm text-brand-text mt-3 leading-relaxed">{p.description}</p>
                      <p className="text-xs text-brand-textMuted mt-3">Enviado por: <strong>{p.submitted_by_name || "anônimo"}</strong></p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid={`approve-place-${p.place_id}`} onClick={() => actPlace(p.place_id, "approve")} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
                        <Check className="w-4 h-4 mr-1" /> Aprovar
                      </Button>
                      <Button data-testid={`reject-place-${p.place_id}`} variant="outline" onClick={() => actPlace(p.place_id, "reject")} className="rounded-full border-brand-border text-brand-text hover:bg-red-50 hover:text-red-700 hover:border-red-200 font-bold">
                        <X className="w-4 h-4 mr-1" /> Rejeitar
                      </Button>
                      <Button data-testid={`edit-place-${p.place_id}`} variant="outline" onClick={() => setEditing(p)} className="rounded-full border-brand-border text-brand-text hover:bg-brand-bg font-bold">
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ALL PLACES */}
        <TabsContent value="all" className="mt-6">
          {loading ? <p>Carregando...</p> : allPlaces.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-brand-border p-12 text-center text-brand-textMuted">Nenhum lugar cadastrado ainda.</div>
          ) : (
            <div className="grid gap-3">
              {allPlaces.map((p) => (
                <div key={p.place_id} data-testid={`admin-place-${p.place_id}`} className="rounded-2xl bg-white border border-brand-border p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        {(p.restrictions || []).map((r) => <RestrictionBadge key={r} type={r} />)}
                        <StatusPill status={p.status} />
                      </div>
                      <h3 className="font-heading font-bold text-lg text-brand-text">{p.name}</h3>
                      <p className="text-sm text-brand-textMuted inline-flex items-center gap-1 mt-1">
                        <MapPin className="w-3.5 h-3.5" /> {p.city} · {p.state}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid={`edit-admin-${p.place_id}`} variant="outline" onClick={() => setEditing(p)} className="rounded-full border-brand-border text-brand-text hover:bg-brand-bg font-bold">
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`delete-admin-${p.place_id}`} variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300 font-bold">
                            <Trash2 className="w-4 h-4 mr-1" /> Excluir
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white rounded-3xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">Excluir lugar?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação remove o lugar <strong>{p.name}</strong> e todas as suas avaliações permanentemente. Não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full font-bold">Cancelar</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-${p.place_id}`} onClick={() => deletePlace(p.place_id)} className="bg-red-600 hover:bg-red-700 text-white rounded-full font-bold">
                              Excluir permanentemente
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* REVIEWS */}
        <TabsContent value="reviews" className="mt-6">
          {loading ? <p>Carregando...</p> : allReviews.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-brand-border p-12 text-center text-brand-textMuted">Nenhuma avaliação ainda.</div>
          ) : (
            <div className="grid gap-4">
              {allReviews.map((r) => (
                <div key={r.review_id} data-testid={`admin-review-${r.review_id}`} className="rounded-3xl bg-white border border-brand-border p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-brand-text">{r.user_name}</span>
                        <span className="text-xs text-brand-textMuted">sobre</span>
                        <span className="font-bold text-brand-primary">{r.place_name}</span>
                        <StatusPill status={r.status} />
                      </div>
                      <div className="flex items-center gap-0.5 mt-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={`star-${n}`} className={`w-4 h-4 ${n <= r.rating ? "fill-brand-yellow text-brand-yellow" : "text-brand-border"}`} />
                        ))}
                      </div>
                      <p className="text-brand-text mt-3 leading-relaxed">{r.comment}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {r.status === "pending" && (
                        <>
                          <Button data-testid={`approve-review-${r.review_id}`} onClick={() => actReview(r.review_id, "approve")} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
                            <Check className="w-4 h-4 mr-1" /> Aprovar
                          </Button>
                          <Button data-testid={`reject-review-${r.review_id}`} variant="outline" onClick={() => actReview(r.review_id, "reject")} className="rounded-full border-brand-border text-brand-text hover:bg-red-50 hover:text-red-700 hover:border-red-200 font-bold">
                            <X className="w-4 h-4 mr-1" /> Rejeitar
                          </Button>
                        </>
                      )}
                      <Button data-testid={`edit-review-${r.review_id}`} variant="outline" onClick={() => setEditingReview(r)} className="rounded-full border-brand-border text-brand-text hover:bg-brand-bg font-bold">
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`delete-review-btn-${r.review_id}`} variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300 font-bold">
                            <Trash2 className="w-4 h-4 mr-1" /> Excluir
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white rounded-3xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">Excluir avaliação?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação remove permanentemente a avaliação de <strong>{r.user_name}</strong>. Não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full font-bold">Cancelar</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-review-${r.review_id}`} onClick={() => deleteReview(r.review_id)} className="bg-red-600 hover:bg-red-700 text-white rounded-full font-bold">
                              Excluir permanentemente
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TOPICS */}
        <TabsContent value="topics" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-brand-textMuted">{topics.length} tópico(s) cadastrado(s)</p>
            <Button data-testid="new-topic-btn" onClick={() => setCreatingTopic(true)} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-bold">
              <Plus className="w-4 h-4 mr-1" /> Novo tópico
            </Button>
          </div>
          {loading ? <p>Carregando...</p> : (
            <div className="grid md:grid-cols-3 gap-4">
              {topics.map((t) => (
                <div key={t.topic_id} data-testid={`admin-topic-${t.topic_id}`} className="rounded-3xl bg-white border border-brand-border p-6 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-brand-primary" />
                      <h3 className="font-heading font-bold text-xl text-brand-text">{t.title}</h3>
                    </div>
                    {t.built_in === false && <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-brand-secondary/15 text-brand-secondary">CUSTOM</span>}
                  </div>
                  {t.subtitle && <p className="text-sm text-brand-textMuted mb-3">{t.subtitle}</p>}
                  <p className="text-sm text-brand-text/80 line-clamp-3 flex-1">{(t.description || "").replace(/<[^>]+>/g, "")}</p>
                  <p className="text-xs text-brand-textMuted mt-3">{(t.tips || []).length} dica(s) · {(t.sections || []).length} seção(ões)</p>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <Button
                      data-testid={`edit-topic-${t.topic_id}`}
                      onClick={() => setEditingTopic(t)}
                      className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-bold flex-1"
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    {t.built_in === false && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`delete-topic-btn-${t.topic_id}`} variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300 font-bold">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white rounded-3xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">Excluir tópico?</AlertDialogTitle>
                            <AlertDialogDescription>
                              O tópico <strong>{t.title}</strong> será removido permanentemente. A URL <code>/topico/{t.topic_id}</code> deixará de existir.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full font-bold">Cancelar</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-topic-${t.topic_id}`} onClick={() => deleteTopic(t.topic_id)} className="bg-red-600 hover:bg-red-700 text-white rounded-full font-bold">
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* BANNERS */}
        <TabsContent value="banners" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-brand-textMuted">{banners.length} banner(s) cadastrado(s)</p>
            <Button data-testid="new-banner-btn" onClick={() => setCreatingBanner(true)} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-bold">
              <Plus className="w-4 h-4 mr-1" /> Novo banner
            </Button>
          </div>
          {loading ? <p>Carregando...</p> : (
            <div className="grid md:grid-cols-2 gap-4">
              {banners.map((b) => (
                <div key={b.banner_id} data-testid={`admin-banner-${b.banner_id}`} className="rounded-3xl border border-brand-border p-5 flex flex-col" style={{ background: b.bg_color, color: b.text_color }}>
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] opacity-90"><Megaphone className="w-4 h-4" /> {b.banner_id === "hero" ? "Home Hero" : b.banner_id === "petition" ? "Abaixo-assinado" : b.banner_id}</span>
                    <div className="flex items-center gap-2">
                      {b.built_in === false && <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-white/25 text-white">CUSTOM</span>}
                      <span className="text-xs font-bold rounded-full px-2 py-0.5" style={{ background: b.enabled ? b.accent_color : "#999", color: b.enabled ? b.button_text : "#fff" }}>{b.enabled ? "ATIVO" : "OCULTO"}</span>
                    </div>
                  </div>
                  <p className="text-xs opacity-80 mb-2">Posição: <strong>{placementLabel(b.placement)}</strong></p>
                  <h3 className="font-heading font-black text-xl">{b.title} {b.highlight && <span style={{ color: b.accent_color }}>{b.highlight}</span>}</h3>
                  <p className="text-sm mt-2 opacity-90 line-clamp-3">{b.description}</p>
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    {b.button_label && <span className="inline-block rounded-full font-bold px-3 py-1 text-xs" style={{ background: b.button_bg, color: b.button_text }}>{b.button_label}</span>}
                  </div>
                  <div className="mt-5 flex gap-2 flex-wrap">
                    <Button
                      data-testid={`edit-banner-${b.banner_id}`}
                      onClick={() => setEditingBanner(b)}
                      className="rounded-full bg-white/95 hover:bg-white text-brand-text font-bold flex-1"
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    {b.built_in === false && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`delete-banner-btn-${b.banner_id}`} variant="outline" className="rounded-full bg-white/95 border-red-200 text-red-700 hover:bg-white font-bold">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white rounded-3xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">Excluir banner?</AlertDialogTitle>
                            <AlertDialogDescription>O banner <strong>{b.title}</strong> será removido permanentemente.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full font-bold">Cancelar</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-banner-${b.banner_id}`} onClick={() => deleteBanner(b.banner_id)} className="bg-red-600 hover:bg-red-700 text-white rounded-full font-bold">
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* FOOTER */}
        <TabsContent value="footer" className="mt-6">
          <div className="rounded-3xl bg-white border border-brand-border p-8 flex flex-col items-start gap-4" data-testid="admin-footer-card">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-2xl bg-brand-primary/10 text-brand-primary flex items-center justify-center"><LayoutTemplate className="w-6 h-6" /></span>
              <div>
                <h3 className="font-heading font-bold text-xl text-brand-text">Rodapé do site</h3>
                <p className="text-sm text-brand-textMuted">Nome da marca, textos, colunas de links, copyright e cores.</p>
              </div>
            </div>
            <Button
              data-testid="edit-footer-btn"
              onClick={() => setEditingFooter(true)}
              className="mt-2 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-bold"
            >
              <Pencil className="w-4 h-4 mr-1" /> Editar rodapé
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <EditPlaceModal
        open={!!editing}
        place={editing}
        onClose={() => setEditing(null)}
        onSaved={() => load()}
      />
      <EditReviewModal
        open={!!editingReview}
        review={editingReview}
        onClose={() => setEditingReview(null)}
        onSaved={() => load()}
      />
      <EditTopicModal
        open={!!editingTopic}
        topic={editingTopic}
        onClose={() => setEditingTopic(null)}
        onSaved={() => load()}
      />
      <EditBannerModal
        open={!!editingBanner}
        banner={editingBanner}
        onClose={() => setEditingBanner(null)}
        onSaved={() => load()}
      />
      <CreateTopicModal
        open={creatingTopic}
        onClose={() => setCreatingTopic(false)}
        onCreated={(t) => { load(); setEditingTopic(t); }}
      />
      <CreateBannerModal
        open={creatingBanner}
        onClose={() => setCreatingBanner(false)}
        onCreated={(b) => { load(); setEditingBanner(b); }}
      />
      <EditFooterModal
        open={editingFooter}
        onClose={() => setEditingFooter(false)}
        onSaved={() => load()}
      />
    </div>
  );
}
